import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Alert,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import {
  ArrowBack,
  DirectionsBus,
  LocationOn,
  People,
  Schedule,
  Warning,
} from '@mui/icons-material';
import { collection, doc, endAt, getDoc, onSnapshot, orderBy, query, startAt } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { Operation, StationLog, StopRecord } from '../types/operation';
import { useStationPassengers } from '../hooks/useStationPassengers';
import { emptyPassengerCounts, formatAverageMinutes, koreanDate, PassengerCounts, summarizeArrivalTiming } from '../utils/stationPassengers';

interface StationPassRecord {
  id: string;
  operationId: string;
  routeId: string;
  routeName: string;
  vehicleNumber: string;
  driverName?: string;
  stationId: string;
  stationName: string;
  sequence: number;
  scheduledTime?: string;
  actualArrivalTime?: Date;
  departureTime?: Date;
  delayMinutes?: number;
  passengers: number;
  status: 'arrived' | 'skipped' | 'pending';
  source: 'stationLogs' | 'stopRecords';
}

interface StationDetailRow extends PassengerCounts {
  id: string;
  operationId?: string;
  routeId: string;
  routeName: string;
  vehicleNumber?: string;
  driverName?: string;
  stationName: string;
  sequence: number;
  scheduledTime?: string;
  actualArrivalTime?: Date;
  delayMinutes?: number;
  passengers: number;
  status: 'arrived' | 'skipped' | 'pending' | 'missing';
}

const getLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const getTimestampDate = (value: unknown): Date | undefined => {
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate();
  }
  return undefined;
};

const getDelayChip = (record: StationDetailRow) => {
  if (record.status === 'missing') {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  if (record.status === 'skipped') {
    return <Chip label="통과" color="default" size="small" />;
  }

  if (record.delayMinutes === undefined) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  if (record.delayMinutes > 0) {
    return (
      <Chip
        icon={<Warning />}
        label={`+${record.delayMinutes}분`}
        color="warning"
        size="small"
      />
    );
  }

  if (record.delayMinutes < 0) {
    return <Chip label={`${record.delayMinutes}분`} color="info" size="small" />;
  }

  return <Chip label="정시" color="success" size="small" />;
};

const getStatusLabel = (record: StationDetailRow): string => {
  if (record.status === 'missing') return '기록 없음';
  if (record.status === 'skipped') return '통과';
  if (record.status === 'pending') return '도착 전';
  return '도착';
};

export const StationOperationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { stationId = '' } = useParams<{ stationId: string }>();
  const [searchParams] = useSearchParams();
  const { adminType } = useAuth();
  const selectedDate = searchParams.get('date') || koreanDate(new Date());
  const stationNameParam = searchParams.get('name') || '';
  const showTestData = searchParams.get('test') === '1';
  const decodedStationId = decodeURIComponent(stationId);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [routeNames, setRouteNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const routeFilter = useMemo(() => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null;
    }
  }, [adminType]);

  const passengers = useStationPassengers(selectedDate, showTestData, routeFilter);
  const routes = passengers.routes;
  const targetStationName = stationNameParam || passengers.stations.find(station =>
    station.aliases.includes(decodedStationId))?.name || decodedStationId;
  const dayEntries = useMemo(() => passengers.entries.filter(entry => entry.date === selectedDate &&
    entry.stationName === targetStationName), [passengers.entries, selectedDate, targetStationName]);

  useEffect(() => {
    if (!selectedDate) return;

    setLoading(true);
    setOperations([]);
    let active = true;

    const q = query(
      collection(db, 'operations'),
      orderBy('__name__'),
      startAt(selectedDate),
      endAt(`${selectedDate}\uf8ff`)
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const data = snapshot.docs
          .map((operationDoc) => {
            const docData = operationDoc.data();
            const operationDate = operationDoc.id.split('_')[0];

            return {
              id: operationDoc.id,
              operationDate,
              routeId: docData.routeId || '',
              routeName: docData.routeName || '',
              vehicleNumber: docData.vehicleNumber || '',
              driverName: docData.driverName || '',
              status: docData.status || '',
              startTime: docData.startTime,
              endTime: docData.endTime,
              currentStopIndex: docData.currentStopIndex || 0,
              totalStops: docData.totalStops || 0,
              isDelayed: docData.isDelayed || false,
              delayMinutes: docData.delayMinutes || 0,
              totalPassengers: docData.totalPassengers || 0,
              isHot: docData.isHot || 0,
              isCold: docData.isCold || 0,
              isDanger: docData.isDanger || 0,
              isTestMode: docData.isTestMode || false,
              stationLogs: docData.stationLogs || [],
              stopRecords: docData.stopRecords || [],
            } as Operation;
          })
          .filter((operation) => (showTestData ? operation.isTestMode === true : !operation.isTestMode));

        const routeNameMap: Record<string, string> = {};
        const missingRouteIds = new Set<string>();

        data.forEach((operation) => {
          if (operation.routeName) {
            routeNameMap[operation.routeId] = operation.routeName;
          } else if (operation.routeId) {
            missingRouteIds.add(operation.routeId);
          }
        });

        for (const routeId of Array.from(missingRouteIds)) {
          try {
            const routeDoc = await getDoc(doc(db, showTestData ? 'test_routes' : 'routes', routeId));
            routeNameMap[routeId] = routeDoc.exists() ? routeDoc.data()?.name || routeId : routeId;
          } catch (error) {
            console.error(`Error fetching route ${routeId}:`, error);
            routeNameMap[routeId] = routeId;
          }
        }

        const filteredData = routeFilter
          ? data.filter((operation) => {
              const routeName = operation.routeName || routeNameMap[operation.routeId] || operation.routeId;
              return routeName.includes(routeFilter);
            })
          : data;

        if (!active) return;
        setOperations(filteredData);
        setRouteNames(routeNameMap);
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to station operation detail:', error);
        setLoading(false);
      }
    );

    return () => { active = false; unsubscribe(); };
  }, [selectedDate, routeFilter, showTestData]);

  const records = useMemo<StationPassRecord[]>(() => {
    const allRecords = operations.flatMap((operation) => {
      const routeName = routeNames[operation.routeId] || operation.routeName || operation.routeId;

      if (operation.stationLogs && operation.stationLogs.length > 0) {
        return operation.stationLogs.map((log: StationLog, index: number) => {
          const actualArrivalTime = getTimestampDate(log.actualArrivalTime);

          return {
            id: `${operation.id}_${log.stationId || index}`,
            operationId: operation.id,
            routeId: operation.routeId,
            routeName,
            vehicleNumber: operation.vehicleNumber,
            driverName: operation.driverName,
            stationId: log.stationId || `${log.stationName}_${index}`,
            stationName: log.stationName || '정류장명 없음',
            sequence: index + 1,
            scheduledTime: log.scheduledTime,
            actualArrivalTime,
            delayMinutes: log.delayMinutes,
            passengers: 0,
            status: log.status === 'skipped' ? 'skipped' : actualArrivalTime ? 'arrived' : 'pending',
            source: 'stationLogs',
          } as StationPassRecord;
        });
      }

      return (operation.stopRecords || []).map((stop: StopRecord, index: number) => {
        const arrivalTime = getTimestampDate(stop.arrivalTime);

        return {
          id: `${operation.id}_${stop.stopId || index}`,
          operationId: operation.id,
          routeId: operation.routeId,
          routeName,
          vehicleNumber: operation.vehicleNumber,
          driverName: operation.driverName,
          stationId: stop.stopId || `${stop.stopName}_${index}`,
          stationName: stop.stopName || '정류장명 없음',
          sequence: index + 1,
          actualArrivalTime: arrivalTime,
          departureTime: getTimestampDate(stop.departureTime),
          passengers: 0,
          status: stop.isSkipped ? 'skipped' : arrivalTime ? 'arrived' : 'pending',
          source: 'stopRecords',
        } as StationPassRecord;
      });
    });

    return allRecords
      .filter((record) => record.stationId === decodedStationId || record.stationName === targetStationName)
      .sort((a, b) => {
        const aTime = a.actualArrivalTime?.getTime() || Number.MAX_SAFE_INTEGER;
        const bTime = b.actualArrivalTime?.getTime() || Number.MAX_SAFE_INTEGER;
        if (aTime !== bTime) return aTime - bTime;

        const scheduledCompare = (a.scheduledTime || '').localeCompare(b.scheduledTime || '');
        if (scheduledCompare !== 0) return scheduledCompare;

        return a.routeName.localeCompare(b.routeName, 'ko');
      });
  }, [operations, routeNames, decodedStationId, targetStationName]);

  const statistics = useMemo(() => {
    const timing = summarizeArrivalTiming(records);
    const totalPassengers = dayEntries.reduce((sum, entry) => sum + entry.journeys, 0);
    const arrivedRecords = records.filter((record) => record.status === 'arrived');
    const recordedRouteIds = new Set(records.map((record) => record.routeId));
    const selectedDay = getLocalDate(selectedDate).getDay();
    const firebaseWeekday = selectedDay === 0 ? 7 : selectedDay;
    const scheduledRouteIds = new Set(
      routes
        .filter((route) => {
          const routeName = route.name || route.id;
          if (routeFilter && !routeName.includes(routeFilter)) return false;
          if (route.operatingDays && !route.operatingDays.includes(firebaseWeekday)) return false;

          return (route.stops || []).some((stop) => (
            stop.stationName === stationNameParam ||
            stop.stationName === decodedStationId ||
            records.some((record) => record.stationName === stop.stationName)
          ));
        })
        .map((route) => route.id)
    );

    return {
      routeCount: new Set([...Array.from(scheduledRouteIds), ...dayEntries.map(entry => entry.routeId), ...records.map(record => record.routeId)]).size,
      passCount: records.length,
      ...timing,
      totalPassengers,
      arrivedCount: arrivedRecords.length,
      missingCount: Array.from(scheduledRouteIds).filter((routeId) => !recordedRouteIds.has(routeId)).length,
    };
  }, [decodedStationId, records, routeFilter, routes, selectedDate, stationNameParam, dayEntries]);

  const detailRows = useMemo<StationDetailRow[]>(() => {
    const day = getLocalDate(selectedDate).getDay() || 7;
    const rows = new Map<string, StationDetailRow>();
    routes.forEach(route => {
      const stop = route.stops.find(stop => stop.stationName === targetStationName);
      if (!stop || (route.operatingDays && !route.operatingDays.includes(day))) return;
      rows.set(route.id, {
        ...emptyPassengerCounts(), id: `missing_${route.id}_${targetStationName}`,
        routeId: route.id, routeName: route.name, stationName: targetStationName,
        sequence: stop.order, scheduledTime: stop.arrivalTime, status: 'missing',
      });
    });
    records.forEach(record => {
      rows.set(record.routeId, { ...emptyPassengerCounts(), ...record });
    });
    dayEntries.forEach(entry => {
      const route = routes.find(route => route.id === entry.routeId);
      const stop = route?.stops.find(stop => stop.stationName === entry.stationName);
      const row = rows.get(entry.routeId) || {
        ...emptyPassengerCounts(), id: `checkin_${entry.routeId}_${entry.stationName}`,
        routeId: entry.routeId, routeName: route?.name || entry.routeId,
        stationName: entry.stationName, sequence: stop?.order || 0,
        scheduledTime: stop?.arrivalTime, status: 'missing' as const,
      };
      rows.set(entry.routeId, { ...row, ...entry });
    });
    return Array.from(rows.values()).sort((a, b) =>
      (a.scheduledTime || '').localeCompare(b.scheduledTime || '') || a.routeName.localeCompare(b.routeName, 'ko'));
  }, [routes, selectedDate, targetStationName, records, dayEntries]);

  const stationTitle = detailRows[0]?.stationName || records[0]?.stationName || stationNameParam || decodedStationId || '정류장';

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn sx={{ fontSize: 32, color: '#667eea' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                {stationTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(selectedDate), 'yyyy년 MM월 dd일 (E)', { locale: ko })} 정류장별 운행 상세
              </Typography>
            </Box>
          </Box>

          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/operation/stations?date=${selectedDate}&test=${showTestData ? '1' : '0'}`)}
          >
            목록
          </Button>
        </Box>

        {passengers.error && <Alert severity="error" sx={{ mb: 2 }}>{passengers.error}</Alert>}
        {!passengers.loading && !passengers.error && (passengers.unmatchedTotal > 0 || passengers.invalidTimestamps > 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            조회 범위의 월 전체 정류장 미매칭 승하차 {passengers.unmatchedTotal}건 (조회일 {passengers.unmatched.get(selectedDate) || 0}건) · 날짜 오류 {passengers.invalidTimestamps}건
          </Alert>
        )}
        {loading || passengers.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <DirectionsBus sx={{ fontSize: 32, color: '#667eea' }} />
                  <Typography variant="h6" fontWeight={600}>
                    경유 노선
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} color="primary">
                  {statistics.routeCount.toLocaleString()}
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Schedule sx={{ fontSize: 32, color: '#10b981' }} />
                  <Typography variant="h6" fontWeight={600}>
                    통과 기록
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#10b981' }}>
                  {statistics.passCount.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  도착 {statistics.arrivedCount.toLocaleString()}건 · 미운영 {statistics.missingCount.toLocaleString()}건
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <People sx={{ fontSize: 32, color: '#3b82f6' }} />
                  <Typography variant="h6" fontWeight={600}>
                    이용 인원
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#3b82f6' }}>
                  {passengers.error ? '-' : statistics.totalPassengers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  해당 정류장 체크인 1건 = 1명 (승하차 중복 제외)
                </Typography>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Warning sx={{ fontSize: 32, color: '#f59e0b' }} />
                  <Typography variant="h6" fontWeight={600}>
                    지연
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  {statistics.delayedRecords.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  평균 {formatAverageMinutes(statistics.averageDelayMinutes)}
                </Typography>
              </Paper>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Schedule sx={{ fontSize: 32, color: '#0284c7' }} />
                  <Typography variant="h6" fontWeight={600}>조기 도착</Typography>
                </Box>
                <Typography variant="h3" fontWeight={700} sx={{ color: '#0284c7' }}>
                  {statistics.earlyArrivalRecords.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  평균 {formatAverageMinutes(statistics.averageEarlyArrivalMinutes)}
                </Typography>
              </Paper>
            </Box>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                노선별 통과 기록
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                표의 이용 인원은 예약·QR 승하차 합계이며, 월 데이터 최초 조회 기준입니다.
                운행기록이 없어도 체크인 이용 내역은 표시합니다.
                지연·조기 도착 평균은 각각 해당 도착 기록만 대상으로 계산하며, 정시·미도착·통과 기록은 제외합니다.
              </Typography>
              {detailRows.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography color="text.secondary">
                    해당 정류장의 운행 예정 노선이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>노선명</TableCell>
                        <TableCell>기사명</TableCell>
                        <TableCell>차량 번호</TableCell>
                        <TableCell align="center">정류장 순번</TableCell>
                        <TableCell align="center">예정 시간</TableCell>
                        <TableCell align="center">통과 시간</TableCell>
                        <TableCell align="center">이용 인원</TableCell>
                        {['예약 승차', '예약 하차', 'QR 승차', 'QR 하차'].map(label => <TableCell key={label} align="center">{label}</TableCell>)}
                        <TableCell align="center">지연 / 조기 도착</TableCell>
                        <TableCell align="center">상태</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailRows.map((record) => (
                        <TableRow
                          key={record.id}
                          hover
                          onClick={() => {
                            if (record.operationId) {
                              navigate(`/operation/${record.operationId}`);
                            }
                          }}
                          sx={{
                            cursor: record.operationId ? 'pointer' : 'default',
                            backgroundColor: record.status === 'missing' ? '#fff7ed' : undefined,
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DirectionsBus sx={{ fontSize: 18, color: '#667eea' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {record.routeName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{record.driverName || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {record.vehicleNumber || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{record.sequence}</TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={600}>
                              {record.scheduledTime || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {record.actualArrivalTime ? format(record.actualArrivalTime, 'HH:mm:ss') : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <People sx={{ fontSize: 16, color: '#667eea' }} />
                              <Typography variant="body2" fontWeight={600}>
                                {passengers.error ? '-' : `${record.passengers.toLocaleString()}명`}
                              </Typography>
                            </Box>
                          </TableCell>
                          {(['reservationBoarding', 'reservationAlighting', 'qrBoarding', 'qrAlighting'] as const).map(field => (
                            <TableCell key={field} align="center">{passengers.error ? '-' : record[field].toLocaleString()}</TableCell>
                          ))}
                          <TableCell align="center">{getDelayChip(record)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getStatusLabel(record)}
                              color={
                                record.status === 'arrived'
                                  ? 'success'
                                  : record.status === 'missing'
                                  ? 'error'
                                  : record.status === 'skipped'
                                  ? 'default'
                                  : 'primary'
                              }
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Box>
    </MainLayout>
  );
};
